// Email verification via a 6-digit CODE (OTP) — replaces the magic link.
//
// Why a code, not a link: a magic link opens a NEW browser tab, which splits
// the auth session across two tabs and produced the verify↔"checking" redirect
// loop. A code stays in ONE tab — read it from the email, type it, done.
//
// Flow: on mount we email a fresh code (single send point; signup no longer
// sends). The user types it → POST /api/auth/verify-code. On a correct code the
// backend flips emailVerified=true via the Firebase Admin API, so there's
// nothing left to re-validate. We then refresh the client token to pick up the
// new claim and HARD-navigate to /gallery — a full reload re-derives all auth +
// brand state, so no stale React state can bounce us back (the loop fix).

import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { verifyEmailCode } from '@/lib/api';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';

const CODE_LEN = 6;

const REASON_MSG: Record<string, string> = {
  wrong_code: "That code isn't right. Double-check the email and try again.",
  expired: 'That code expired. Send a fresh one below.',
  no_pending: 'No code on file — send a new one below.',
  too_many_attempts: 'Too many tries. Send a fresh code below.',
  bad_code: 'Enter the 6-digit code from your email.',
  verify_failed: "We couldn't verify just now — give it a moment and try again.",
  no_user: 'Your session expired. Sign in again to continue.',
  network: 'Network hiccup — try again.',
};

export default function VerifyEmailPage() {
  const { user, resendVerification, refreshVerification, signOut } = useAuth();

  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const sentOnMount = useRef(false);

  // Auto-send the code once, on mount. Single send point — works whether the
  // user arrived from a fresh signup or an AuthGate bounce.
  useEffect(() => {
    if (!user || sentOnMount.current) return;
    sentOnMount.current = true;
    // Already verified (rare — they'd normally be let through): straight in,
    // resuming a stashed checkout, else the free-tier onboarding.
    if (user.emailVerified) {
      window.location.assign(consumePostAuthRedirect() ?? '/brands/new');
      return;
    }
    (async () => {
      setSending(true);
      const { error: err } = await resendVerification();
      setSending(false);
      if (err) setError(err);
      else {
        setNotice(`We emailed a 6-digit code to ${user.email}.`);
        setCooldown(30);
      }
    })();
  }, [user, resendVerification]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    const c = code.trim();
    if (c.length !== CODE_LEN || !/^\d+$/.test(c)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setVerifying(true);
    const { ok, reason } = await verifyEmailCode(c);
    if (ok) {
      // Backend already flipped emailVerified=true — refresh the client token so
      // it carries the new claim, then HARD-navigate (full reload) so no stale
      // useAuth/useBrand state can bounce us back to this page.
      await refreshVerification();
      setVerified(true);
      setVerifying(false);
      // Resume a stashed checkout, else the free-tier onboarding (a fresh
      // signup gets 20 credits and starts building their brand here).
      const dest = consumePostAuthRedirect() ?? '/brands/new';
      setTimeout(() => { window.location.assign(dest); }, 900);
      return;
    }
    setVerifying(false);
    setError(REASON_MSG[reason ?? 'network'] ?? 'Something went wrong — try again.');
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    setSending(true);
    const { error: err } = await resendVerification();
    setSending(false);
    if (err) setError(err);
    else {
      setNotice('New code sent — check your inbox (and spam).');
      setCooldown(30);
    }
  };

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">{children}</div>
    </div>
  );

  // Verified — brief success, then a hard nav takes over.
  if (verified) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-6">
          <i className="ri-checkbox-circle-line text-emerald-600 text-2xl" />
        </div>
        <h2 className="font-display font-bold text-dark text-xl mb-3">Email verified!</h2>
        <p className="text-dark-muted text-sm mb-6">Your account is active. Taking you in…</p>
        <a
          href="/gallery"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
        >
          Continue
          <i className="ri-arrow-right-line" />
        </a>
      </Shell>
    );
  }

  // Not signed in (e.g. session dropped). Send them to sign in.
  if (!user) {
    return (
      <Shell>
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <i className="ri-login-box-line text-primary text-2xl" />
        </div>
        <h2 className="font-display font-bold text-dark text-xl mb-3">Sign in to continue</h2>
        <p className="text-dark-muted text-sm mb-6">
          Your session ended. Sign in and we&rsquo;ll pick up where you left off.
        </p>
        <a
          href="/auth/sign-in"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
        >
          <i className="ri-login-box-line" />
          Sign in
        </a>
      </Shell>
    );
  }

  // Code-entry state.
  return (
    <Shell>
      <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-6">
        <i className="ri-mail-lock-line text-amber-500 text-2xl" />
      </div>
      <h2 className="font-display font-bold text-dark text-xl mb-3">Verify your email</h2>
      <p className="text-dark-muted text-sm mb-6">
        Enter the 6-digit code we emailed to{' '}
        <span className="font-medium text-dark">{user.email}</span>.
      </p>

      {notice && <p className="text-emerald-600 text-xs mb-4">{notice}</p>}
      {error && <p className="text-red-600 text-xs mb-4">{error}</p>}

      <form onSubmit={submit} className="flex flex-col gap-4">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={CODE_LEN}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LEN))}
          placeholder="000000"
          autoFocus
          className="w-full text-center tracking-[0.5em] text-2xl font-bold text-dark px-4 py-3.5 rounded-2xl border border-surface-dark/50 bg-white placeholder:text-dark-muted/30 placeholder:tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
        />
        <button
          type="submit"
          disabled={verifying || code.length !== CODE_LEN}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60"
        >
          {verifying ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Verifying…
            </>
          ) : (
            <>
              <i className="ri-shield-check-line" />
              Verify
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0 || sending}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-surface-dark/50 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer disabled:opacity-50"
        >
          <i className="ri-mail-send-line" />
          {sending ? 'Sending…' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
        </button>
      </form>

      <button
        onClick={() => { void signOut(); }}
        className="mt-6 text-xs text-dark-muted hover:text-dark transition-colors cursor-pointer"
      >
        Use a different account
      </button>
    </Shell>
  );
}
